import { supabase } from './supabase';
import { getCurrentUser } from './auth';

/**
 * A READER'S STANDING "NEVER SHOW ME" LIST.
 *
 * WHY THIS IS ACCOUNT-LEVEL AND NOT SCREEN-LEVEL.
 *
 * FilterSheet already excludes content warnings correctly, per craving. What
 * it could not do is remember, and a safety filter that has to be re-set on
 * every craving is one a reader will eventually forget to set. Tying it to the
 * account rather than to a screen is also what makes it survive the new
 * homepage: the filter sheet can move, be redrawn, or be reached from
 * somewhere else entirely, and a stored exclusion still applies.
 *
 * WHERE IT IS STORED, and the one thing to be careful about.
 *
 * `user_preferences`, which already holds this reader's per-account filter
 * state, under `preference_type = 'exclude_trigger'`. Every other value in
 * that column is something the reader wants MORE of. This one is the opposite,
 * and the `exclude_` prefix is the only thing standing between the two — a row
 * reading `trigger: animal death` is one careless join away from being read as
 * a preference FOR it, which on a safety filter is the worst way to be wrong.
 *
 * NEVER select from user_preferences without filtering preference_type, and
 * never treat an `exclude_` row as a positive preference.
 *
 * Requires migration 20260906040000, which widens that column's CHECK
 * constraint from four values to five. Before it is run, every save here is
 * rejected by Postgres — which is why saveExclusions returns a discriminated
 * result rather than a boolean, and why the caller shows the reason.
 */

const KIND = 'exclude_trigger';

/**
 * Read this reader's saved exclusions.
 *
 * Returns { ok, names } rather than a bare array: an empty list and a failed
 * read must not look the same here of all places. "You have no saved
 * exclusions" and "we could not check your saved exclusions" mean opposite
 * things to somebody who has asked never to be shown animal death, and only
 * one of them is safe to render as an unticked box.
 */
export async function loadExclusions() {
  const user = await getCurrentUser();
  if (!user?.id) return { ok: true, names: [] };   // signed out: genuinely none

  const { data, error } = await supabase
    .from('user_preferences')
    .select('preference_value')
    .eq('user_id', user.id)
    .eq('preference_type', KIND);

  if (error) return { ok: false, names: [], reason: error.message };
  return { ok: true, names: (data || []).map(r => r.preference_value).filter(Boolean) };
}

/**
 * Replace this reader's saved exclusions with exactly `names`.
 *
 * Delete-then-insert rather than a diff. The set is small (a reader picks a
 * handful of warnings, not hundreds), and a diff has an "I removed one"
 * branch that is easy to get wrong in the direction of leaving an exclusion
 * behind that the reader believes they cleared. On a safety filter the
 * simpler mechanism is worth more than the saved round trip.
 *
 * The delete runs first and is checked. If it fails, the insert does NOT run:
 * inserting on top of a failed clear would leave the old exclusions in place
 * alongside the new ones, so the reader would be MORE filtered than they
 * asked, silently. Over-filtering is the safer failure of the two, but it is
 * still not what they asked for, and it must not happen quietly.
 */
export async function saveExclusions(names) {
  const user = await getCurrentUser();
  if (!user?.id) return { ok: false, reason: 'You need to be signed in to save a default.' };

  const { error: clearErr } = await supabase
    .from('user_preferences')
    .delete()
    .eq('user_id', user.id)
    .eq('preference_type', KIND);

  if (clearErr) return { ok: false, reason: `Could not update your saved list, ${clearErr.message}` };

  const wanted = [...new Set((names || []).filter(Boolean))];
  if (wanted.length === 0) return { ok: true, names: [] };

  const { error: insertErr } = await supabase
    .from('user_preferences')
    .insert(wanted.map(preference_value => ({ user_id: user.id, preference_type: KIND, preference_value })));

  if (insertErr) {
    // The most likely cause on a first run, named explicitly, because
    // "violates check constraint" tells the reader nothing and tells whoever
    // they report it to almost nothing.
    const looksLikeConstraint = /check constraint/i.test(insertErr.message);
    return {
      ok: false,
      reason: looksLikeConstraint
        ? 'Your list could not be saved: the database has not been updated to allow saved content-warning exclusions yet (migration 20260906040000).'
        : `Your list could not be saved, ${insertErr.message}`,
    };
  }

  return { ok: true, names: wanted };
}
